import { BHAVA_NAMES } from '@astrally/shared';
import { useI18n } from '../../i18n';

/**
 * The twelve bhavas as a labelled map: number, traditional name (Tanu, Dhan,
 * Sahaj…) and the one-breath gist of the life areas each covers. Bhava 1 is
 * shaded — the whole count hangs from it, exactly as in the layout diagrams.
 */

const CELL_W = 166;
const CELL_H = 74;
const GAP = 6;

/**
 * SVG text does not wrap, so the gist is split into two roughly equal lines
 * at a word boundary. Every gist in BHAVA_NAMES fits two lines at this size.
 */
function splitGist(gist: string): [string, string] {
  const words = gist.split(' ');
  let first = '';
  for (const word of words) {
    if (first.length + word.length + 1 > gist.length / 2) break;
    first = first ? `${first} ${word}` : word;
  }
  if (!first) first = words[0];
  return [first, gist.slice(first.length).trim()];
}

export function BhavaMap() {
  const { t } = useI18n();
  return (
    <svg
      viewBox="0 0 340 500"
      style={{ width: '100%', height: 'auto' }}
      role="img"
      aria-label={t('learn.fig.bhavaMap.alt')}
    >
      {BHAVA_NAMES.map(({ n, name, gist }) => {
        const col = (n - 1) % 2;
        const row = Math.floor((n - 1) / 2);
        const x = col * (CELL_W + GAP) + 1;
        const y = row * (CELL_H + GAP) + 1;
        const [line1, line2] = splitGist(gist);
        const first = n === 1;
        return (
          <g key={n}>
            <rect
              x={x}
              y={y}
              width={CELL_W}
              height={CELL_H}
              rx="10"
              fill={first ? 'var(--mode-soft)' : 'var(--card)'}
              stroke="var(--border-strong)"
              strokeWidth="1"
            />
            <text
              x={x + 12}
              y={y + 24}
              fontSize="11.5"
              fontWeight="700"
              fill={first ? 'var(--mode-ink)' : 'var(--text)'}
            >
              {n} · {name}
            </text>
            <text x={x + 12} y={y + 42} fontSize="8.5" fill="var(--text-dim)">
              {line1}
            </text>
            <text x={x + 12} y={y + 56} fontSize="8.5" fill="var(--text-dim)">
              {line2}
            </text>
          </g>
        );
      })}
      <text x={170} y={494} textAnchor="middle" fontSize="10" fill="var(--text-dim)">
        {t('learn.fig.bhavaMap.note')}
      </text>
    </svg>
  );
}
